import { ActivityGameMatchInfo, BracketDraftInfo, BracketDraftSlot, MatchParticipant } from '../../interface';

export function bracketRoundName(roundNames: string[] | undefined, round: number): string {
    const name = roundNames?.[round]?.trim();
    return name || `Round ${round + 1}`;
}

export function bracketSessionLabel(
    roundName: string,
    session: number,
    boutNumber?: string,
): string {
    const bout = boutNumber?.trim();
    if (bout) {
        return `Bout ${bout}`;
    }
    const name = roundName.trim() || `Round ${session}`;
    return `${name} (${session})`;
}

export function qualifierWinnerLabel(roundName: string, sourceIndex: number): string {
    const name = roundName.trim() || 'Round 1';
    return `Winner of ${name} (${sourceIndex + 1})`;
}

// Plan times are the event's wall clock. The API stores that clock on a UTC
// timestamp, so the digits are read in UTC and never shifted into the browser zone.
export function formatPlanClock(value?: string | null): string | null {
    if (!value) {
        return null;
    }
    const bare = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value.trim());
    if (bare) {
        const hours = Number(bare[1]);
        const minutes = Number(bare[2]);
        if (hours > 23 || minutes > 59) {
            return null;
        }
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }
    return `${String(parsed.getUTCHours()).padStart(2, '0')}:${String(parsed.getUTCMinutes()).padStart(2, '0')}`;
}

type BracketParticipantLabelFields = Pick<
    MatchParticipant,
    | 'id'
    | 'display_name'
    | 'team_name'
    | 'first_name'
    | 'last_name'
    | 'license_number'
>;

export function bracketParticipantName(
    participant: BracketParticipantLabelFields,
): string {
    return (
        participant.display_name?.trim() ||
        participant.team_name?.trim() ||
        [participant.first_name, participant.last_name]
            .map((value) => value?.trim())
            .filter(Boolean)
            .join(' ') ||
        participant.id
    );
}

export function formatBracketParticipantLabel(
    participant: BracketParticipantLabelFields,
): string {
    const name = bracketParticipantName(participant);
    const licenseNumber = participant.license_number?.trim();

    return licenseNumber ? `${name} · ${licenseNumber}` : name;
}

export function swapBracketParticipants(
    slots: BracketDraftSlot[],
    sourceKey: string,
    targetKey: string,
): BracketDraftSlot[] {
    if (sourceKey === targetKey) {
        return slots;
    }
    const source = slots.find((slot) => slot.key === sourceKey);
    const target = slots.find((slot) => slot.key === targetKey);
    if (
        !source ||
        !target ||
        source.kind !== 'participant' ||
        target.kind !== 'participant' ||
        source.is_seed_locked ||
        target.is_seed_locked
    ) {
        return slots;
    }

    return slots.map((slot) => {
        if (slot.key === sourceKey) {
            return { ...slot, participant_id: target.participant_id };
        }
        if (slot.key === targetKey) {
            return { ...slot, participant_id: source.participant_id };
        }
        return slot;
    });
}

export function assignBracketParticipant(
    slots: BracketDraftSlot[],
    targetKey: string,
    participantID?: string,
): BracketDraftSlot[] {
    const target = slots.find((slot) => slot.key === targetKey);
    if (!target || target.kind !== 'participant' || target.is_seed_locked) {
        return slots;
    }
    if (!participantID) {
        if (!target.participant_id) {
            return slots;
        }
        return slots.map((slot) =>
            slot.key === targetKey ? { ...slot, participant_id: undefined } : slot,
        );
    }
    const source = slots.find((slot) => slot.participant_id === participantID);
    if (source) {
        return swapBracketParticipants(slots, source.key, targetKey);
    }
    return slots.map((slot) =>
        slot.key === targetKey ? { ...slot, participant_id: participantID } : slot,
    );
}

function isByeMatch(match: ActivityGameMatchInfo): boolean {
    return match.state === 'bye' || (match.participants || []).some(participant => participant.draft_slot_kind === 'bye');
}

function byeAdvanceParticipant(match: ActivityGameMatchInfo): MatchParticipant | undefined {
    return (match.participants || []).find(participant => (
        participant.draft_slot_kind !== 'bye' &&
        participant.draft_slot_kind !== 'qualifier' &&
        Boolean(participant.id)
    ));
}

function withoutDraftSlotFields(participant: MatchParticipant): MatchParticipant {
    return {
        ...participant,
        draft_slot_key: undefined,
        draft_slot_kind: undefined,
        is_seed_locked: undefined,
    };
}

function namedParticipant(
    person: MatchParticipant,
    template?: MatchParticipant,
): MatchParticipant {
    return withoutDraftSlotFields({
        ...(template || person),
        id: person.id,
        display_name: bracketParticipantName(person),
        first_name: person.first_name,
        last_name: person.last_name,
        team_name: person.team_name,
        license_number: person.license_number,
        location: person.location,
        noc: person.noc,
        gender: person.gender,
        birth_date: person.birth_date,
        category_ids: person.category_ids,
    });
}

function applyLocalByeAdvancement(
    originalMatches: ActivityGameMatchInfo[],
    matches: ActivityGameMatchInfo[],
    peopleById: Map<string, MatchParticipant>,
): ActivityGameMatchInfo[] {
    const originalById = new Map(originalMatches.map(match => [match.id, match]));
    type ByeAdvance = {
        originalWinnerId: string;
        winner: MatchParticipant | undefined;
    };
    const byNext = new Map<string, ByeAdvance[]>();

    for (const match of matches) {
        const original = originalById.get(match.id);
        if (!original || !isByeMatch(original) || !match.next_match_id) {
            continue;
        }
        const list = byNext.get(match.next_match_id) || [];
        list.push({
            originalWinnerId: byeAdvanceParticipant(original)?.id || '',
            winner: byeAdvanceParticipant(match),
        });
        byNext.set(match.next_match_id, list);
    }

    return matches.map(match => {
        const original = originalById.get(match.id);
        let next = match;
        if (original && isByeMatch(original)) {
            const winner = byeAdvanceParticipant(match);
            next = { ...next, winner_id: winner?.id || '' };
        }

        const items = byNext.get(match.id);
        if (!items) {
            return next;
        }

        const originalWinnerIds = new Set(items.map(item => item.originalWinnerId).filter(Boolean));
        const kept = (next.participants || []).filter(participant => (
            Boolean(participant.draft_slot_key) || !originalWinnerIds.has(participant.id)
        ));
        const added = items.flatMap(item => {
            const winner = item.winner;
            if (!winner?.id) {
                return [];
            }
            const person = peopleById.get(winner.id) || winner;
            return [namedParticipant(person, winner)];
        });
        return { ...next, participants: kept.concat(added) };
    });
}

export function matchesWithDraftSlots(
    matches: ActivityGameMatchInfo[],
    slots: BracketDraftInfo['slots'],
    participants: BracketDraftInfo['participants'],
): ActivityGameMatchInfo[] {
    const byId = new Map(participants.map(participant => [participant.id, participant]));
    const slotByKey = new Map(slots.map(slot => [slot.key, slot]));
    const withSlots = matches.map(match => ({
        ...match,
        participants: (match.participants || []).map(participant => {
            if (!participant.draft_slot_key) {
                return participant;
            }
            const slot = slotByKey.get(participant.draft_slot_key);
            if (!slot || slot.kind !== 'participant') {
                return participant;
            }
            const person = slot.participant_id ? byId.get(slot.participant_id) : undefined;
            return {
                ...participant,
                id: person?.id || '',
                display_name: person ? bracketParticipantName(person) : '',
                is_seed_locked: slot.is_seed_locked,
            };
        }),
        draft_slots: (match.draft_slots || []).map(ref => {
            const slot = slotByKey.get(ref.key);
            if (!slot) {
                return ref;
            }
            return {
                ...ref,
                participant_id: slot.participant_id,
                is_seed_locked: slot.is_seed_locked,
            };
        }),
    }));
    return applyLocalByeAdvancement(matches, withSlots, byId);
}
